import React, { memo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const SubjectHeader = memo(({ icon, title, subtitle, accent = 'from-pink-400 via-purple-500 to-indigo-600', leftContent = null, rightContent = null, onBack = false, mobileMenu = null, compactOnScroll = false, mobileSubtitle = null, progressPercentage = null }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!compactOnScroll) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setIsCompact(scrollY > 10);
        // Calculate scroll progress (0-100%)
        const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
        setScrollProgress(Math.min((scrollY / maxScroll) * 100, 100));
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [compactOnScroll]);

  return (
    <div className={`sticky top-0 z-30 backdrop-blur-md bg-gradient-to-br ${accent} bg-opacity-85 pt-[env(safe-area-inset-top)] transition-all duration-300 ${isCompact ? 'shadow-lg' : 'shadow-md'}`} style={{ 
      background: `linear-gradient(to bottom right, rgba(0,0,0,0.1), rgba(0,0,0,0.05))`,
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
    }}>
        <div className={`max-w-4xl mx-auto px-4 ${isCompact ? 'py-2.5' : 'py-4'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {onBack && (
                <button
                  onClick={() => { try { window.history.length > 1 ? window.history.back() : (window.location.hash = '/'); } catch (_) { window.location.hash = '/'; } }}
                  className="bg-white/25 hover:bg-white/35 active:bg-white/40 text-white rounded-full px-3.5 py-2 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent transform hover:scale-105 active:scale-95 shrink-0"
                  aria-label="Back"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                >
                  ←
                </button>
              )}
              {leftContent}
              <div 
                className={`${isCompact ? 'text-3xl sm:text-4xl' : 'text-4xl sm:text-5xl'} shrink-0 drop-shadow-lg transition-transform duration-300 ${isCompact ? 'scale-90' : 'scale-100'}`} 
                aria-hidden="true"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
              >
                {icon}
              </div>
              <h1 
                className={`${isCompact ? 'text-sm sm:text-base md:text-lg' : 'text-base sm:text-lg md:text-xl'} font-extrabold text-white break-words transition-all duration-300`}
                style={{ 
                  textShadow: '0 2px 12px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.8)',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2',
                  WebkitTextStroke: '0.5px rgba(0,0,0,0.3)'
                }}
              >
                {title}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {subtitle && (
                <div 
                  className="hidden sm:block text-white text-sm sm:text-base md:text-lg font-semibold text-right transition-opacity duration-300"
                  style={{ textShadow: '0 2px 6px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)' }}
                >
                  {subtitle}
                </div>
              )}
              {/* Mobile subtitle chip */}
              {mobileSubtitle && (
                <div 
                  className="sm:hidden text-xs bg-purple-900/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full font-bold shadow-lg border border-purple-700/50" 
                  aria-label="Summary"
                  style={{ 
                    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  {mobileSubtitle}
                </div>
              )}
              <div className="hidden sm:flex items-center gap-3">
                {rightContent}
              </div>
              {mobileMenu && (
                <div className="sm:hidden relative" ref={menuRef}>
                  <button
                    className="bg-purple-900/95 hover:bg-purple-800/95 active:bg-purple-700/95 text-white rounded-full px-4 py-3 font-semibold shadow-xl hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-2 focus:ring-offset-purple-900 transform hover:scale-105 active:scale-95 border-2 border-white/30"
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen}
                    aria-label="More actions"
                    onClick={() => setIsMenuOpen((v) => !v)}
                    style={{ 
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  {isMenuOpen && (
                    <div
                      role="menu"
                      tabIndex={-1}
                      className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-2 z-40 border border-white/30 animate-in fade-in slide-in-from-top-2 duration-200"
                      style={{ boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.1)' }}
                    >
                      {mobileMenu}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Progress bar on scroll */}
        {compactOnScroll && scrollProgress > 0 && (
          <div className="h-1 bg-white/25 relative overflow-hidden">
            <div 
              className="h-full bg-white/80 backdrop-blur-sm transition-all duration-150 ease-out shadow-sm"
              style={{ 
                width: `${scrollProgress}%`,
                boxShadow: '0 0 8px rgba(255,255,255,0.5)'
              }}
              role="progressbar"
              aria-valuenow={Math.round(scrollProgress)}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label="Scroll progress"
            />
          </div>
        )}
        {/* Subject progress bar if provided */}
        {progressPercentage !== null && (
          <div className="h-1.5 bg-white/25 relative overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-300 via-orange-400 to-pink-400 transition-all duration-500 ease-out shadow-sm"
              style={{ 
                width: `${Math.min(Math.max(progressPercentage, 0), 100)}%`,
                boxShadow: '0 0 10px rgba(251, 146, 60, 0.6)'
              }}
              role="progressbar"
              aria-valuenow={Math.round(progressPercentage)}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label="Progress"
            />
          </div>
        )}
    </div>
  );
});

SubjectHeader.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  accent: PropTypes.string,
  leftContent: PropTypes.node,
  rightContent: PropTypes.node,
  onBack: PropTypes.bool,
  mobileMenu: PropTypes.node,
  compactOnScroll: PropTypes.bool,
  mobileSubtitle: PropTypes.node,
  progressPercentage: PropTypes.number,
};

SubjectHeader.displayName = 'SubjectHeader';

export default SubjectHeader;


